// hooks/useAiOdds.ts

import { useState, useEffect } from "react";
import { CARD_INFO } from "../dataset";
import { produce } from "immer";
import { useHistory } from "react-router-dom";
import { requestAi, AiRequest, AiResponse } from "../api/ai";

interface PlayerHand {
  userIndex: number;
  hands: number[];
}

interface SelectMode {
  type: string;
  index: number;
  player?: number;
}

export const useAiOdds = () => {
  const [playerHand, setPlayerHand] = useState<PlayerHand>({
    userIndex: 1,
    hands: [-1, -1],
  });
  const [flop, setFlop] = useState<number[]>([-1, -1, -1]);
  const [turn, setTurn] = useState<number | null>(null);
  const [river, setRiver] = useState<number | null>(null);
  const [isCardSelect, setIsCardSelect] = useState(false);
  const [selectMode, setSelectMode] = useState<SelectMode | null>(null);
  const [selectedCards, setSelectedCards] = useState<number[]>([]);
  const [isIconSelect, setIsIconSelect] = useState<string>("heart");

  // AI 관련 상태
  const [aiStyle, setAiStyle] = useState<"aggressive" | "passive" | "balanced">(
    "balanced"
  );
  const [aiResponse, setAiResponse] = useState<string>("");
  const [isAiLoading, setIsAiLoading] = useState(false);

  const history = useHistory();

  // 카드 인덱스를 문자열로 변환하는 함수
  const cardIndexToString = (index: number): string => {
    if (index === -1) return "";
    return CARD_INFO[index];
  };

  // 선택된 카드들을 추적
  useEffect(() => {
    const cards = [...playerHand.hands, ...flop];
    if (turn !== null) cards.push(turn);
    if (river !== null) cards.push(river);
    setSelectedCards(cards.filter((card) => card !== -1));
  }, [playerHand.hands, flop, turn, river]);

  const handleCardClick = (cardIndex: number) => {
    if (selectedCards.includes(cardIndex)) {
      return;
    }

    if (selectMode) {
      if (selectMode.type === "player") {
        setPlayerHand(
          produce(playerHand, (draft) => {
            draft.hands[selectMode.index] = cardIndex;
          })
        );
      } else if (selectMode.type === "flop") {
        setFlop(
          produce(flop, (draft) => {
            draft[selectMode.index] = cardIndex;
          })
        );
      } else if (selectMode.type === "turn") {
        setTurn(cardIndex);
      } else if (selectMode.type === "river") {
        setRiver(cardIndex);
      }
      setIsCardSelect(false);
      setSelectMode(null);
    }
  };

  const selectCardForPlayer = (handIndex: number) => {
    setSelectMode({
      type: "player",
      index: handIndex,
    });
    setIsCardSelect(true);
  };

  const selectCardForFlop = (flopIndex: number) => {
    setSelectMode({
      type: "flop",
      index: flopIndex,
    });
    setIsCardSelect(true);
  };

  const selectCardForTurn = () => {
    if (flop.includes(-1)) {
      alert("플랍을 먼저 선택해주세요.");
      return;
    }
    setSelectMode({
      type: "turn",
      index: 0,
    });
    setIsCardSelect(true);
  };

  const selectCardForRiver = () => {
    if (flop.includes(-1)) {
      alert("플랍을 먼저 선택해주세요.");
      return;
    }
    if (turn === null) {
      alert("턴을 먼저 선택해주세요.");
      return;
    }
    setSelectMode({
      type: "river",
      index: 0,
    });
    setIsCardSelect(true);
  };

  const clear = () => {
    setSelectMode(null);
    setFlop([-1, -1, -1]);
    setPlayerHand({ userIndex: 1, hands: [-1, -1] });
    setSelectedCards([]);
    setTurn(null);
    setRiver(null);
    setIsCardSelect(false);
    setAiResponse("");
  };

  const handleOverlayClick = () => {
    setIsCardSelect(false);
    setSelectMode(null);
  };

  const handleClose = () => {
    history.push("/");
  };

  // AI 요청 함수
  const handleAiRequest = async () => {
    // 플레이어 핸드가 설정되지 않은 경우
    if (playerHand.hands.includes(-1)) {
      alert("플레이어의 핸드를 먼저 선택해주세요.");
      return;
    }

    // 플랍이 부분적으로만 설정된 경우 체크
    const flopCount = flop.filter((card) => card !== -1).length;
    if (flopCount > 0 && flopCount < 3) {
      alert("플랍은 3장을 모두 선택하거나 선택하지 않아야 합니다.");
      return;
    }

    // 턴이 있는데 플랍이 없는 경우
    if (turn !== null && flopCount === 0) {
      alert("턴을 선택하려면 플랍을 먼저 선택해주세요.");
      return;
    }

    // 리버가 있는데 턴이 없는 경우
    if (river !== null && turn === null) {
      alert("리버를 선택하려면 턴을 먼저 선택해주세요.");
      return;
    }

    try {
      setIsAiLoading(true);

      const aiRequest: AiRequest = {
        hand: playerHand.hands.map(cardIndexToString),
        flop: flopCount === 3 ? flop.map(cardIndexToString) : [],
        turn: turn !== null ? cardIndexToString(turn) : null,
        river: river !== null ? cardIndexToString(river) : null,
        style: aiStyle,
      };

      const response: AiResponse = await requestAi(aiRequest);
      setAiResponse(response.reply);
    } catch (error) {
      console.error("AI 요청 실패:", error);
      alert("AI 요청에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsAiLoading(false);
    }
  };

  return {
    playerHand,
    flop,
    turn,
    river,
    isCardSelect,
    selectMode,
    selectedCards,
    handleCardClick,
    selectCardForPlayer,
    selectCardForFlop,
    selectCardForTurn,
    selectCardForRiver,
    clear,
    handleOverlayClick,
    handleClose,
    isIconSelect,
    setIsIconSelect,
    // AI 관련
    aiStyle,
    setAiStyle,
    aiResponse,
    isAiLoading,
    handleAiRequest,
  };
};
